import { useEffect, useState } from 'react';
import logo from '@/assets/images/logo.png';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import useUserStore from '@/stores/UserStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, LogOut } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';

import { trackLogOutAndMixpanelReset } from '@/lib/analytics-event';
import { removeToken } from '@/lib/utils';
import { updateTrialData } from '@/hooks/trial';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhoneInput } from '@/components/common/PhoneInput';

// Form validation schema
const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  // role: z.string().min(1, 'Please select your role'),
  phone: z.string().min(1, 'Phone number is required'),
  howDidYouHear: z.string().min(1, 'Please select how you heard about us'),
  // companyIndustry: z.string().min(1, 'Please select your company industry'),
  salesTeamSize: z.string().min(1, 'Please select your sales team size'),
});

// const roles = [
//   'Entrepreneur',
//   'Closer',
//   'Sales Executive',
//   'Sales Manager',
//   'CMO (Chief Marketing Officer)',
//   'CEO (Chief Executive Officer)',
//   'Business Development Manager',
// ];

const howDidYouHearOptions = [
  'Facebook',
  'Google',
  'LinkedIn',
  'Twitter',
  'Word of Mouth',
  'Instagram',
  'Online Advertisement',
  'Industry Event',
  'Other',
];

// const companyIndustries = [
//   'Advertising',
//   'Education',
//   'Real Estate',
//   'SaaS',
//   'Healthcare',
//   'Retail',
//   'Finance',
// ];

const salesTeamSizes = ['Just Me', '2-5', '5-10', '10-50', '50+'];

export default function OnboardingSetup() {
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const navigate = useNavigate();

  const nextOnboardingStage = useUserStore(
    (state) => state.nextOnboardingStage
  );
  const user = useUserStore((state) => state.user);
  const setCurrentOnboardingStage = useUserStore(
    (state) => state.setCurrentOnboardingStage
  );
  const setNextOnboardingStage = useUserStore(
    (state) => state.setNextOnboardingStage
  );
  const resetUserStore = useUserStore((state) => state.resetUserStore);
  const resetContactStore = useContactStore((state) => state.resetContactStore);
  const resetInboxStore = useInboxStore((state) => state.resetInboxStore);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      // role: '',
      phone: '',
      howDidYouHear: '',
      // companyIndustry: '',
      salesTeamSize: '',
    },
  });

  useEffect(() => {
    // Wait for user data to be loaded before checking onboarding stage
    if (user) {
      setIsDataLoaded(true);
      // Pre-fill form with user data only if fields are currently empty
      const currentFirstName = form.getValues('firstName');
      const currentLastName = form.getValues('lastName');
      if (!currentFirstName && user.first_name) {
        form.setValue('firstName', user.first_name);
      }
      if (!currentLastName && user.last_name) {
        form.setValue('lastName', user.last_name);
      }
    }
  }, [user, form]);

  useEffect(() => {
    // Navigate user to appropriate page based on their onboarding stage
    if (isDataLoaded && !isLoading) {
      if (nextOnboardingStage === 'connect_account') {
        navigate('/onboarding/connect-channel');
      } else if (nextOnboardingStage === 'pending_selection') {
        navigate('/onboarding/instagram/select-page');
      } else if (nextOnboardingStage !== 'additional_details') {
        navigate('/inbox');
      }
    }
  }, [isDataLoaded, nextOnboardingStage, navigate, isLoading]);

  async function onSubmit(values) {
    console.log('Setup form values:', values);
    setIsLoading(true);

    try {
      // Prepare data for API call
      const trialData = {
        first_name: values.firstName,
        last_name: values.lastName,
        phone: values.phone,
        // role: values.role,
        // company_industry: values.companyIndustry,
        team_size: values.salesTeamSize,
        source_hear_about_us: values.howDidYouHear,
      };

      const response = await updateTrialData(trialData);

      if (response.data) {
        // Update onboarding stages from response
        setCurrentOnboardingStage(response.data.current_onboarding_stage);
        setNextOnboardingStage(response.data.next_onboarding_stage);

        toast.success('Setup completed successfully!');

        // Navigate based on next onboarding stage
        if (response.data.next_onboarding_stage === 'connect_account') {
          navigate('/onboarding/connect-channel');
        } else if (
          response.data.next_onboarding_stage === 'pending_selection'
        ) {
          navigate('/onboarding/connect-channel/');
        } else {
          navigate('/inbox');
        }
      }
    } catch (error) {
      console.error('Error updating details:', error);
      toast.error(
        error?.response?.data?.message ||
          'Failed to complete setup. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  const handleLogout = () => {
    removeToken();

    // reset user store
    resetUserStore();
    // reset contact store
    resetContactStore();
    // reset inbox store
    resetInboxStore();

    // reset mixpanel
    trackLogOutAndMixpanelReset();
    // navigate to login page
    navigate('/login');

    // refresh the page to ensure all state is cleared
    window.location.reload();
  };

  // Show loading state while user data is being fetched
  if (!isDataLoaded) {
    return (
      <div className='flex h-screen items-center justify-center bg-background'>
        <div className='flex items-center gap-2'>
          <Loader2 className='h-6 w-6 animate-spin' />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='relative flex h-screen items-center justify-center bg-background'>
      {/* Logout Button - Top Right */}
      <div className='absolute right-4 top-4'>
        <Button
          variant='ghost'
          size='sm'
          onClick={handleLogout}
          className='flex items-center gap-2'
        >
          <LogOut className='h-4 w-4' />
          Logout
        </Button>
      </div>

      <div className='w-full max-w-2xl space-y-8'>
        {/* Header with Logo */}
        <div className='flex items-center justify-center gap-3'>
          <img className='h-12 w-auto' src={logo} alt='Revio' />
          <h1 className='text-3xl font-bold'>Welcome to Revio</h1>
        </div>

        {/* Welcome Section */}
        <div className='space-y-4 text-center'>
          <p className='text-lg text-muted-foreground'>
            Thanks for signing up! We are super excited to have you on board!
            Let&apos;s customize your experience with just 5 quick questions:
          </p>
        </div>

        {/* Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
            {/* Name Fields */}
            <div className='grid grid-cols-2 gap-4'>
              <FormField
                control={form.control}
                name='firstName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input placeholder='Enter your first name' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='lastName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input placeholder='Enter your last name' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Two Column Layout */}
            <div className='grid grid-cols-2 gap-4'>
              {/* Left Column */}
              <div className='space-y-4'>
                {/* <FormField
                  control={form.control}
                  name='role'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your role</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}

                <FormField
                  control={form.control}
                  name='howDidYouHear'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How did you hear about Revio?</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {howDidYouHearOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right Column */}
              <div className='space-y-4'>
                <FormField
                  control={form.control}
                  name='phone'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone number</FormLabel>
                      <FormControl>
                        <PhoneInput
                          placeholder='Enter a phone number'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* <FormField
                  control={form.control}
                  name='companyIndustry'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company industry</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {companyIndustries.map((industry) => (
                            <SelectItem key={industry} value={industry}>
                              {industry}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
              </div>
            </div>

            {/* Sales Team Size */}
            <FormField
              control={form.control}
              name='salesTeamSize'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What is the size of your Sales team?</FormLabel>
                  <FormControl>
                    <div className='flex flex-wrap gap-2'>
                      {salesTeamSizes.map((size) => (
                        <Button
                          key={size}
                          type='button'
                          variant={field.value === size ? 'default' : 'outline'}
                          className='h-10 px-4'
                          onClick={() => field.onChange(size)}
                        >
                          {size}
                        </Button>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Continue Button */}
            <Button type='submit' disabled={isLoading}>
              {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Continue
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
